import { createFileRoute, notFound, Link } from "@tanstack/react-router";
import { getPostsByCategory, posts, type Post } from "@/data/posts";
import { PostCard } from "@/components/PostCard";
import { siteConfig, categorySlug } from "@/config";

export const Route = createFileRoute("/category/$category")({
  loader: ({ params }) => {
    const list = getPostsByCategory(params.category);
    if (list.length === 0) throw notFound();
    const displayName = list[0].category;
    return { list, displayName };
  },
  head: ({ loaderData, params }) => {
    if (!loaderData) {
      return { meta: [{ title: "Category not found" }, { name: "robots", content: "noindex" }] };
    }
    const { displayName } = loaderData;
    const url = `${siteConfig.domain}/category/${params.category}`;
    return {
      meta: [
        { title: `${displayName} — Reviews & Guides | ${siteConfig.siteName}` },
        {
          name: "description",
          content: `Latest ${displayName} reviews, comparisons, and buyer's guides from ${siteConfig.siteName}.`,
        },
        { property: "og:title", content: `${displayName} — ${siteConfig.siteName}` },
        {
          property: "og:description",
          content: `Latest ${displayName} articles.`,
        },
        { property: "og:url", content: url },
      ],
      links: [{ rel: "canonical", href: url }],
    };
  },
  notFoundComponent: () => (
    <div className="mx-auto max-w-3xl px-4 py-24 text-center">
      <h1 className="text-3xl font-bold text-foreground">Category not found</h1>
      <p className="mt-3 text-muted-foreground">
        We couldn't find any posts in that category yet.
      </p>
      <Link to="/" className="mt-6 inline-flex text-primary hover:underline">← Back home</Link>
    </div>
  ),
  component: CategoryPage,
});

function CategoryPage() {
  const { list, displayName } = Route.useLoaderData();
  return (
    <section className="mx-auto max-w-6xl px-4 py-12">
      <nav aria-label="Breadcrumb" className="mb-4 text-sm text-muted-foreground">
        <Link to="/" className="hover:text-primary">Home</Link>
        <span aria-hidden> / </span>
        <span className="text-foreground">{displayName}</span>
      </nav>
      <h1 className="text-3xl font-bold tracking-tight text-foreground md:text-4xl">
        {displayName}
      </h1>
      <p className="mt-2 max-w-2xl text-muted-foreground">
        {list.length} article{list.length === 1 ? "" : "s"} in {displayName}.
      </p>

      <div className="mt-8 grid grid-cols-1 gap-6 md:grid-cols-2 lg:grid-cols-3">
        {list.map((p: any) => <PostCard key={p.id} post={p} />)}
      </div>

      <div className="mt-12 border-t border-border pt-8">
        <h2 className="mb-4 text-sm font-semibold uppercase tracking-wider text-muted-foreground">
          Browse all categories
        </h2>
        <div className="flex flex-wrap gap-2">
          {Array.from(new Set(posts.map((p) => p.category))).map((c) => (
            <Link
              key={c}
              to="/category/$category"
              params={{ category: categorySlug(c) }}
              className="rounded-full border border-border bg-card px-3 py-1 text-sm text-foreground hover:border-primary hover:text-primary"
            >
              {c}
            </Link>
          ))}
        </div>
      </div>
    </section>
  );
}
